import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { BinLocationCard } from 'views/warehouse/components/BinLocationCard';
import { MoveWarningData } from 'views/warehouse/MoveWarningContext/types';
import { StockLotRequestCard } from 'views/warehouse/MoveWarningContext/components/StockLotDisplay/StockLotRequestCard';
import { OrgDisplay, TeamDisplay } from 'views/warehouse/OrgAndTeamContext';
import { getOrgId, getTeamId } from 'views/warehouse/MoveWarningContext/utils';
import { useIsFacilityUsingTeams } from 'views/warehouse/hooks/useIsFacilityUsingTeams';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

type CustomerMismatchContentProps = {
  warningData: MoveWarningData;
};

export const CustomerMismatchContent: FC<CustomerMismatchContentProps> = ({
  warningData,
}) => {
  const theme = useTheme();
  const { isUsingTeams } = useIsFacilityUsingTeams();
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const stockLotOrgId = getOrgId(warningData.stockLot.org);
  const destinationOrgId = getOrgId(warningData.destination.org);
  const stockLotTeamId = getTeamId(warningData.stockLot.team);
  const destinationTeamId = getTeamId(warningData.destination.team);

  const mismatchLabel = isUsingTeams
    ? handleReplaceWithCustomTeamName('Team Mismatch')
    : 'Org Mismatch';

  const stockLotBelongsToLabel = isUsingTeams
    ? handleReplaceWithCustomTeamName('Stock lot belongs to:')
    : 'Stock lot belongs to:';

  const destinationBelongsToLabel = isUsingTeams
    ? handleReplaceWithCustomTeamName('Destination belongs to:')
    : 'Destination belongs to:';

  const instructionText = isUsingTeams
    ? handleReplaceWithCustomTeamName(
        'Scan a location that belongs to the same team or return the stock lot to its original location'
      )
    : 'Scan a location that belongs to the same organization or return the stock lot to its original location';

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Required Action:
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {isUsingTeams
          ? handleReplaceWithCustomTeamName(
              'This stock lot belongs to a different team than the destination location.'
            )
          : 'This stock lot belongs to a different organization than the destination location.'}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <StockLotRequestCard warningData={warningData} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconX
            size={32}
            color={theme.palette.error.main}
            style={{ marginBottom: '4px' }}
          />
          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
            {mismatchLabel}
          </Typography>
        </Box>
        <BinLocationCard stockLocation={warningData.destination} />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {stockLotBelongsToLabel}
        </Typography>
        {isUsingTeams ? (
          <TeamDisplay teamId={stockLotTeamId || null} />
        ) : (
          <OrgDisplay orgId={stockLotOrgId || ''} />
        )}
        <Typography variant="body2" color="text.secondary">
          {destinationBelongsToLabel}
        </Typography>
        {isUsingTeams ? (
          <TeamDisplay teamId={destinationTeamId || null} />
        ) : (
          <OrgDisplay orgId={destinationOrgId || ''} />
        )}
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: 'center' }}
      >
        {instructionText}
      </Typography>
    </Box>
  );
};
